<template>
  <div class="noprint">
    <span :class="{ 'font-weight-bold': !requirementFulfilled }">
      {{ title }}
      <template v-if="!requirementFulfilled"
        ><b-icon
          icon="asterisk"
          class="span-required"
          v-b-tooltip.hover.v-secondary.noninteractive="
            $t('toolbar.reports.requiredFieldTooltip')
          "
          >*
        </b-icon>
      </template>
      <template v-else-if="required">
        <b-icon icon="check" variant="success" scale="1.5"></b-icon>
        <span
          class="btn-x-header ml-2"
          @click="removeAllItems"
          v-b-tooltip.hover.v-secondary.noninteractive="
            $t('toolbar.reports.clearSelectedTooltip')
          "
        >
          <b-icon icon="x" scale="1.4" class="icon-x-clear"></b-icon>
        </span>
      </template>
    </span>

    <v-select
      ref="vselect"
      class="report-selector"
      v-model="current_selected"
      :options="filteredOptions"
      :get-option-label="label_function"
      :label="label_field"
      :disabled="disabled || loading"
      :required="required"
      @option:selected="slotOptionSelected"
      @search="onVSelectSearch"
      :placeholder="$t('toolbar.reports.searchPlaceholder', { title })"
      :filterable="false"
      :close-on-select="false"
    >
      <template #no-options>
        <span v-if="loading">{{
          $t("toolbar.reports.loading", { title })
        }}</span>
        <span v-else-if="localSearchTerm && filteredOptions.length === 0">
          {{
            $t("toolbar.reports.noResultsFound", {
              title,
              search: localSearchTerm,
            })
          }}
        </span>
        <span v-else>{{ $t("toolbar.reports.typeToFilter", { title }) }}</span>
      </template>
      <template #option="option">
        <div>
          <b>{{
            label_function ? label_function(option) : option[label_field]
          }}</b>
          <br />
          <cite style="font-size: small" v-if="option.email">{{
            option.email
          }}</cite>
        </div>
      </template>
      <template #list-footer v-if="totalUsers > page_size">
        <li class="pagination-footer">
          <div class="d-flex justify-content-between align-items-center">
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="!hasPrevPage || loading"
              @click="prevPage"
            >
              {{ $t("toolbar.reports.previous") }}
            </button>
            <small class="text-muted">
              {{ (currentPage - 1) * page_size + 1 }}-{{
                Math.min(currentPage * page_size, totalUsers)
              }}
              {{ $t("reusable.common.of") }} {{ totalUsers }}
            </small>
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="!hasNextPage || loading"
              @click="nextPage"
            >
              {{ $t("toolbar.reports.next") }}
            </button>
          </div>
        </li>
      </template>
    </v-select>

    <div class="container-badge text-left">
      <b-badge
        v-for="item in selected_items"
        :key="item[id_field]"
        class="badge-select"
        variant="info"
        :disabled="disabled"
      >
        <span :id="`popover_users_label-${item.id}-${title}`">
          {{ label_function ? label_function(item) : item[label_field] }}
        </span>
        <b-button
          variant="none"
          size="sm"
          class="round btn-x"
          @click="slotRemoveItem(item)"
        >
          <b-icon icon="x" scale="0.9"></b-icon>
        </b-button>
        <b-popover
          :target="`popover_users_label-${item.id}-${title}`"
          placement="bottom"
          triggers="hover focus"
          boundary="viewport"
        >
          <template>
            <div v-if="item.email">
              {{ item.email }}
            </div>
          </template>
        </b-popover>
      </b-badge>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "TagSelectorWithPagination",
  components: {
    vSelect,
  },
  props: {
    title: {
      type: String,
    },
    id_field: {
      type: String,
      default: "id",
    },
    label_field: {
      type: String,
      default: "name",
    },
    label_function: {
      type: Function,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    max: {
      type: [String, Number],
      default: "all",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    clear_filters: {
      type: Boolean,
      default: false,
    },
    page_size: {
      type: Number,
      default: 20,
    },
    api_endpoint: {
      type: String,
      default: "/authentication/user/",
    },
    url_params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selected_items: [],
      current_selected: [],
      localSearchTerm: "",
      paginatedOptions: [],
      currentPage: 1,
      totalUsers: 0,
      loading: false,
      max_: 1,
      show_all: this.max == "all",
      multiple_: this.multiple,
    };
  },
  computed: {
    requirementFulfilled() {
      if (!this.required) return true;
      if (this.selected_items.length > 0) return true;
      return false;
    },
    filteredOptions() {
      if (!this.localSearchTerm) {
        return this.paginatedOptions;
      }

      const searchLower = this.localSearchTerm.toLowerCase();
      return this.paginatedOptions.filter((user) => {
        const userLabel = this.label_function
          ? this.label_function(user)
          : user[this.label_field];
        const matchesName = userLabel.toLowerCase().includes(searchLower);
        const matchesEmail =
          user.email && user.email.toLowerCase().includes(searchLower);
        return matchesName || matchesEmail;
      });
    },
    hasPrevPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.currentPage * this.page_size < this.totalUsers;
    },
  },
  methods: {
    async fetchUsers(page = 1) {
      this.loading = true;

      try {
        // Construir URL con parámetros
        let url = this.api_endpoint;
        const urlParams = new URLSearchParams();

        // Parámetros básicos de paginación
        urlParams.append("page", page);
        urlParams.append("page_size", this.page_size);

        // Agregar parámetros adicionales desde url_params
        if (this.url_params) {
          Object.keys(this.url_params).forEach((key) => {
            const value = this.url_params[key];
            if (value !== null && value !== undefined && value !== "") {
              urlParams.append(key, value);
            }
          });
        }

        const fullUrl = `${url}?${urlParams.toString()}`;
        const response = await this.$restful.Get(fullUrl);

        // Filtrar usuarios que ya están seleccionados
        const filteredUsers = response.results.filter(
          (user) =>
            !this.selected_items.find(
              (selected) => selected[this.id_field] === user[this.id_field]
            )
        );
        this.paginatedOptions = filteredUsers;

        this.totalUsers = response.count;
        this.currentPage = page;
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$bvToast.toast(this.$t("toolbar.reports.loadingError"), {
          title: this.$t("reusable.common.error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    onVSelectSearch(search) {
      // Solo actualizar el término de búsqueda local para filtrado local
      this.localSearchTerm = search;
    },

    async changePage(page) {
      await this.fetchUsers(page);
    },

    async prevPage() {
      if (this.hasPrevPage) {
        await this.fetchUsers(this.currentPage - 1);
      }
    },

    async nextPage() {
      if (this.hasNextPage) {
        await this.fetchUsers(this.currentPage + 1);
      }
    },

    slotOptionSelected(option) {
      if (
        option[this.id_field] == "all" ||
        (this.id_field === this.label_field &&
          option[this.id_field] == "-- TODOS --")
      ) {
        // Para "TODOS" necesitaríamos cargar todos los usuarios,
        // lo cual no es práctico con paginación
        return;
      } else if (
        (this.selected_items.length == this.max_) == 1 &&
        !this.show_all
      ) {
        this.selected_items = [];
        this.selected_items.push(option);
      } else if (this.selected_items.length == this.max_ && !this.show_all) {
        return;
      } else {
        this.selected_items.push(option);
      }

      // Remover el usuario seleccionado de las opciones
      const index = this.paginatedOptions.findIndex(
        (user) => user[this.id_field] === option[this.id_field]
      );
      if (index !== -1) {
        this.paginatedOptions.splice(index, 1);
      }

      this.$emit("input", this.selected_items);

      // Limpiar selección
      this.$nextTick(() => {
        this.$refs.vselect.clearSelection();
      });
    },

    slotRemoveItem(item) {
      const index = this.selected_items.findIndex(
        (x) => x[this.id_field] == item[this.id_field]
      );
      if (index != -1) {
        this.selected_items.splice(index, 1);
        this.$emit("input", this.selected_items);

        // Agregar el usuario de vuelta a las opciones de la página actual
        this.paginatedOptions.unshift(item);
      }
    },

    removeAllItems() {
      // Recargar la página actual para actualizar las opciones
      this.fetchUsers(this.currentPage);

      this.selected_items = [];
      this.$emit("input", this.selected_items);
    },
  },
  watch: {
    max() {
      if (this.max == "all") {
        this.multiple_ = true;
        this.max_ = Infinity;
        this.show_all = true;
      } else {
        this.max_ = Number(this.max);
        this.show_all = false;
      }
      if (this.max_ == 1) {
        this.multiple_ = false;
        this.show_all = false;
      }
      // Si el nuevo máximo es menor que la selección actual (ej: al cambiar
      // de un reporte con selección múltiple a uno de selección única), se
      // conservan los primeros ítems hasta el límite en vez de arrastrar
      // una selección inválida o vaciar el filtro completo.
      if (
        this.max != "all" &&
        this.max_ > 0 &&
        this.selected_items.length > this.max_
      ) {
        this.selected_items = this.selected_items.slice(0, this.max_);
        this.$emit("input", this.selected_items);
      }
    },
    clear_filters() {
      if (this.clear_filters) {
        this.removeAllItems();
        this.localSearchTerm = "";
      }
    },
    url_params: {
      handler() {
        // Cuando cambien los parámetros de URL, recargar desde la primera página
        this.fetchUsers(1);
      },
      deep: true,
    },
  },
  async created() {
    // Cargar usuarios iniciales
    await this.fetchUsers();
  },
};
</script>

<style scoped>
.report-selector >>> {
  --vs-dropdown-max-height: 300px;
}
.report-selector {
  border-radius: 8px;
}
.badge-select {
  margin: 2px;
  padding: 2px 3px;
}
.container-badge {
  max-height: 110px;
  overflow-y: auto;
}
.btn-x-header {
  height: 20px;
  width: 16.5px;
  cursor: pointer;
  background: rgba(128, 128, 128, 0.1);
  border-radius: 3px;
  color: red;
}
.btn-x-header:hover {
  color: rgba(255, 0, 0, 0.726);
}
.btn-x {
  padding: 0px !important;
}
.span-required {
  font-weight: bold;
  font-size: 6pt;
  transform: translateY(-80%);
  color: red;
}
.pagination-footer {
  padding: 8px 12px;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  margin: 0;
}
@media print {
  .container-badge {
    max-height: 100% !important;
    overflow-y: hidden !important;
  }
}
</style>